import React from "react";
import type { ServiceGridItem, ServiceStatus, RegionStatus } from "../../types/status";

interface StatusGridProps {
  services: ServiceGridItem[];
}

const STATUS_LABEL: Record<ServiceStatus, string> = {
  operational: "Operational",
  degraded: "Degraded performance",
  outage: "Service disruption",
  maintenance: "Under maintenance",
};

function StatusDot({ status }: { status: ServiceStatus }) {
  const label = STATUS_LABEL[status] ?? status;

  if (status === "operational") {
    return (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" role="img" aria-label={label} title={label} style={{ display: "inline-block", verticalAlign: "middle", flexShrink: 0 }}>
        <circle cx="8" cy="8" r="8" fill="#107c10" />
        <path d="M4.5 8.5l2.5 2.5 4.5-5" stroke="white" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  if (status === "degraded") {
    return (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" role="img" aria-label={label} title={label} style={{ display: "inline-block", verticalAlign: "middle", flexShrink: 0 }}>
        <circle cx="8" cy="8" r="8" fill="#d17a00" />
        <path d="M8 4.5v4M8 10.5v1" stroke="white" strokeWidth="1.75" strokeLinecap="round" />
      </svg>
    );
  }

  if (status === "outage") {
    return (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" role="img" aria-label={label} title={label} style={{ display: "inline-block", verticalAlign: "middle", flexShrink: 0 }}>
        <circle cx="8" cy="8" r="8" fill="#a4262c" />
        <path d="M5.5 5.5l5 5M10.5 5.5l-5 5" stroke="white" strokeWidth="1.75" strokeLinecap="round" />
      </svg>
    );
  }

  return (
    <span
      className={`sp-dot sp-dot--${status}`}
      role="img"
      aria-label={label}
      title={label}
    />
  );
}

export function StatusGrid({ services }: StatusGridProps) {
  if (services.length === 0) {
    return (
      <div className="sp-grid-empty" role="status">
        No services configured. Add services via the{" "}
        <a href="/admin/registry">service registry</a>.
      </div>
    );
  }

  // Collect all unique regions across every service (components + direct rows)
  const regionMap = new Map<string, string>(); // slug → name
  for (const svc of services) {
    for (const comp of (svc.components ?? [])) {
      for (const r of (comp.regions ?? [])) regionMap.set(r.region_slug, r.region_name);
    }
    for (const r of (svc.regions ?? [])) regionMap.set(r.region_slug, r.region_name);
  }
  // Sort regions by name so columns are consistent
  const regionSlugs = [...regionMap.keys()].sort((a, b) =>
    (regionMap.get(a) ?? a).localeCompare(regionMap.get(b) ?? b)
  );

  const abbreviate = (name: string) =>
    name.length > 10 ? name.toUpperCase().replace(/\s+/g, "-") : name.toUpperCase();

  function buildRegionLookup(regions: RegionStatus[]) {
    return Object.fromEntries(regions.map((r) => [r.region_slug, r]));
  }

  function worstStatus(regions: RegionStatus[]): ServiceStatus {
    const statuses = regions.map((r) => r.status);
    if (statuses.includes("outage")) return "outage";
    if (statuses.includes("degraded")) return "degraded";
    if (statuses.includes("maintenance")) return "maintenance";
    return "operational";
  }

  return (
    <div className="sp-grid-wrapper">
      <table className="sp-grid" aria-label="Service health by region">
        <thead>
          <tr>
            <th scope="col" className="sp-grid__service-col">Service Name</th>
            {regionSlugs.map((slug) => (
              <th key={slug} scope="col" className="sp-grid__region-col" title={regionMap.get(slug)}>
                {abbreviate(regionMap.get(slug) ?? slug)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {services.map((service) => {
            const hasComponents = (service.components ?? []).length > 0;
            const hasDirectRegions = (service.regions ?? []).length > 0;

            return (
              <React.Fragment key={service.id}>
                {/* ── Service header row ── */}
                {hasComponents && (
                  <tr className="sp-grid__service-header">
                    <th
                      scope="rowgroup"
                      colSpan={regionSlugs.length + 1}
                      className="sp-grid__service-header-cell"
                    >
                      {service.name}
                    </th>
                  </tr>
                )}

                {/* ── Component rows ── */}
                {(service.components ?? []).map((comp) => {
                  const regionLookup = buildRegionLookup(comp.regions ?? []);
                  const rowStatus = worstStatus(comp.regions ?? []);
                  return (
                    <tr key={comp.id} className={`sp-grid__row sp-grid__row--${rowStatus}`}>
                      <th scope="row" className="sp-grid__service-name sp-grid__component-name">
                        {comp.name}
                      </th>
                      {regionSlugs.map((slug) => {
                        const r = regionLookup[slug];
                        return (
                          <td key={slug} className="sp-grid__cell">
                            {r ? (
                              <StatusDot status={r.status} />
                            ) : (
                              <span
                                className="sp-dot sp-dot--unknown"
                                role="img"
                                aria-label="Not deployed"
                                title="Not deployed in this region"
                              />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}

                {/* ── Direct service-level rows (legacy / no components) ── */}
                {hasDirectRegions && !hasComponents && (
                  <tr
                    className={`sp-grid__row sp-grid__row--${worstStatus(service.regions)}`}
                  >
                    <th scope="row" className="sp-grid__service-name">
                      <span className="sp-grid__service-label">{service.name}</span>
                    </th>
                    {regionSlugs.map((slug) => {
                      const r = buildRegionLookup(service.regions)[slug];
                      return (
                        <td key={slug} className="sp-grid__cell">
                          {r ? (
                            <StatusDot status={r.status} />
                          ) : (
                            <span
                              className="sp-dot sp-dot--unknown"
                              role="img"
                              aria-label="No data"
                              title="No data"
                            />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
